import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import _get from 'lodash/get';

import A11yAnnouncement from '../../components/A11yAnnouncement/A11yAnnouncement';
import Container from '../../components/Container/Container';
import {
    fetchQuestions,
    saveQuestionsProgress,
    resetQuestions,
} from '../../state-management/actions/assessmentSectionQuestions';
import QuestionnaireForm from './QuestionnaireForm/QuestionnaireForm';
import * as questionType from '../../constants/questionType';
import * as questionValue from '../../constants/questionValue';
import { useNavigate, useLocation } from 'react-router-dom';
import Spinner from '../../components/Spinner/Spinner';

function getAnswerModel(optionId, optionValue) {
    return {
        selectedValue: optionValue === undefined ? 1 : parseInt(optionValue, 10),
        questionOptionId: parseInt(optionId.replace('option-', ''), 10),
    };
}

function formatValues(valuesObj) {
    delete valuesObj.age;
    const formattedValues = [];
    Object.keys(valuesObj).forEach((questionId) => {
        const answer = valuesObj[questionId];
        if (typeof answer === 'string') {
            formattedValues.push(getAnswerModel(answer));
        } else if (answer !== null && typeof answer === 'object') {
            Object.keys(answer).forEach((optionId) => {
                formattedValues.push(getAnswerModel(optionId, answer[optionId]));
            });
        }
    });

    return formattedValues;
}

const AssessmentSectionQuestions = (props) => {
    const navigate = useNavigate();
    const { state: routerState } = useLocation();
    const { saved, ...prospect } = routerState || {};
    const { title: __title, description: __description, score: __score, interpretation: __interpretation } = prospect;
    const { resetAssessmentQuestions,
        onQuestionsFetch,
        report: { title: _title, description: _description, score: _score, interpretation: _interpretation },
        saved: _saved } = props;

    const score = _score || __score;
    const description = _description || __description
    const interpretation = _interpretation || __interpretation;
    const title = _title || __title;

    const [state, setState] = useState({
        message: '',
        isMounted: false,
        questionType: 'MOST_LEAST',
    })


    useEffect(() => {
        if (!saved && !score) {
            navigate('/', { saved, message: 'Failed to save the user' });
        }
        if (!_saved && score) {
            navigate('/report', { state: { ...prospect, score, description, interpretation, title } });
        }
    }, [navigate, prospect, saved, _saved, score, description, interpretation, title]);

    useEffect(() => {
        resetAssessmentQuestions();
        onQuestionsFetch();
    }, [resetAssessmentQuestions, onQuestionsFetch]);


    const getInitialValues = () => {
        const { questions } = props;
        const values = {};
        questions.forEach((question) => {
            if (question.type === questionType.MOST_LEAST) {
                question.options.forEach((option) => {
                    const selectedValue = `${option.selectedValue}`;
                    if (
                        selectedValue === questionValue.MOST ||
                        selectedValue === questionValue.LEAST
                    ) {
                        if (values[question.id] === undefined) {
                            values[question.id] = {};
                        }

                        values[question.id][`option-${option.id}`] = selectedValue;
                    }
                });
            } else if (question.type === questionType.TRUE_FALSE ||
                question.type === questionType.TABULAR_TRUE_FALSE) {
                question.options.forEach((option) => {
                    const selectedValue = `${option.selectedValue}`;

                    if (
                        selectedValue === questionValue.TRUE_FALSE
                    ) {
                        values[question.id] = `${option.id}`;
                    }
                });
            } else if (question.type === questionType.DROPDOWN) {
                question.options.forEach((option) => {
                    const selectedValue = `${option.selectedValue}`;
                    if (selectedValue === '1') {
                        if (values[question.id] === undefined) {
                            values[question.id] = {};
                        }

                        values[question.id][`option-${option.id}`] = `${option.id} + 14`;
                    }
                });
            } else if (question.type === questionType.AGREE_SCALE ||
                question.type === questionType.TABULAR_CHECKBOX) {
                let index = 1;
                question.options.forEach((option) => {
                    const selectedValue = `${option.selectedValue}`;
                    if (selectedValue === '1') {
                        if (values[question.id] === undefined) {
                            values[question.id] = {};
                        }

                        values[question.id][`option-${option.id}`] = `${index}`;
                    }
                    index += 1;
                });
            } else if (question.type === questionType.SINGLE_RESPONSE) {
                question.options.forEach((option) => {
                    const selectedValue = `${option.selectedValue}`;
                    if (selectedValue === '1') {
                        if (values[question.id] === undefined) {
                            values[question.id] = {};
                        }

                        values[question.id][`option-${option.id}`] = `${option.id}`;
                    }
                });
            }
            if ([questionType.TABULAR_CHECKBOX, questionType.TABULAR_TRUE_FALSE]
                .includes(question.type)) {
                question.options.forEach((option) => {
                    const selectedValue = `${option.selectedValue}`;
                    if (selectedValue === '1') {
                        if (values[question.id] === undefined) {
                            values[question.id] = {};
                        }
                        values[`${question.id}_option`] = `${option.id}`;
                    }
                });
            }
        });
        return values;
    }

    const getValidationErrors = (values = {}) => {
        const { questions } = props;

        const errors = {};

        questions?.forEach((question) => {
            if (question.type === questionType.MOST_LEAST) {
                const valuesObj = values[question.id] || {};
                const valuesArray = Object.values(valuesObj);

                if (
                    !valuesArray.includes(questionValue.MOST) ||
                    !valuesArray.includes(questionValue.LEAST)
                ) {
                    question.options.forEach((option) => {
                        const optionName = `${question.id}.option-${option.id}`;
                        const value = _get(values, optionName);

                        if (!value) {
                            if (errors[question.id] === undefined) {
                                errors[question.id] = {};
                            }

                            errors[question.id][`option-${option.id}`] = 'Required';
                        }
                    });
                }
            } else if (question.type === questionType.TRUE_FALSE ||
                question.type === questionType.SINGLE_RESPONSE ||
                question.type === questionType.DROPDOWN) {
                if (!Object.prototype.hasOwnProperty.call(values, question.id)) {
                    errors[question.id] = 'Required';
                }
            } else if (questions.type === questionType.AGREE_SCALE) {
                question.options.forEach((option) => {
                    const optionName = `${question.id}.option-${option.id}`;
                    const value = _get(values, optionName);

                    if (!value) {
                        if (errors[question.id] === undefined) {
                            errors[question.id] = {};
                        }

                        errors[question.id][`option-${option.id}`] = 'Required';
                    }
                });
            }
        });

        return errors;
    }


    const handleSubmit = (values, actions) => {
        const {
            onSave,
            page,
            sectionId,
            token,
        } = props;
        if (state.questionType !== 'TRUE_FALSE') {
            setState(prevState => ({
                ...prevState,
                questionType: 'TRUE_FALSE',
            }));
        }
        const form = {
            actions,
            values: formatValues(values),
        };
        const meta = {
            page,
            sectionId,
            token,
        };

        onSave({
            ...meta, toReports: (state) => navigate('/report', { state: { ...prospect, ...state } }),
        }, form);
    }
    const {
        questions,
        assessmentLanguage, assessmentTypeId, currentStep, isLoading,
        savePartialQuestionsLoading,
    } = props;
    const { message } = state;
    const initialValues = getInitialValues();

    const isInitialValid = questions.length > 0 &&
        Object.keys(getValidationErrors(initialValues)).length === 0;

    return (
        <Container.Themed className="container--public-new-assessments">

            {(!isLoading && questions.length && (
                <React.Fragment>
                    <A11yAnnouncement message={message} />
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        isInitialValid={isInitialValid}
                        onSubmit={handleSubmit}
                        render={formProps => (
                            <QuestionnaireForm
                                {...formProps}
                                questions={questions}
                                assessmentLanguage={assessmentLanguage}
                                assessmentTypeId={assessmentTypeId}
                                currentStep={currentStep}
                                savePartialQuestionsLoading={savePartialQuestionsLoading}
                                questionType={state.questionType}
                            />
                        )}
                        validate={getValidationErrors}
                    />
                </React.Fragment>
            )) || <Spinner />}
        </Container.Themed>
    );
}

AssessmentSectionQuestions.propTypes = {
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    onQuestionsFetch: PropTypes.func,
    onSave: PropTypes.func,
    token: PropTypes.string.isRequired,
    assessmentLanguage: PropTypes.string,
    assessmentTypeId: PropTypes.number,
    currentStep: PropTypes.shape({}),
    resetAssessmentQuestions: PropTypes.func,
    isLoading: PropTypes.bool,
    savePartialQuestionsLoading: PropTypes.bool,
    report: PropTypes.shape({}),
};

AssessmentSectionQuestions.defaultProps = {
    questions: [],
    assessmentLanguage: 'ENG',
    onQuestionsFetch: () => { },
    onSave: () => { },
    resetAssessmentQuestions: () => { },
    assessmentTypeId: 0,
    currentStep: {},
    isLoading: false,
    savePartialQuestionsLoading: false,
    report: {},
};

const mapStateToProps = state => ({
    questions: state?.assessmentSectionQuestions?.questions?.map(i => ({ ...i, options: i?.demoQuestionOptions?.map(k => ({ ...k })) })),
    savePartialQuestionsLoading: state?.assessmentSectionQuestions?.savePartialQuestionsLoading,
    isLoading: state?.assessmentSectionQuestions?.isLoading,
    assessmentLanguage: state?.assessment?.language,
    assessmentTypeId: state?.assessmentStatus?.surveyTypeId,
    report: state?.assessmentSectionQuestions?.report,
    saved: state?.prospect.saved,
});

const mapDispatchToProps = dispatch => ({
    onQuestionsFetch: meta => dispatch(fetchQuestions(meta)),
    onSave: (meta, form) => dispatch(saveQuestionsProgress(meta, form)),
    resetAssessmentQuestions: () => dispatch(resetQuestions()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssessmentSectionQuestions);
